import React, { useState, useCallback, useEffect } from 'react';
import { Check } from 'lucide-react';
// Optional type - can be provided or will use fallback
type TFormComponent = any;
import { DfFormFileUpload } from './df-form-controls/df-form-file-upload';
import { RaiseIssueModal } from './RaiseIssueModal';
import './ComponentActionFeatures.scss';

interface IComponentActionFeaturesProps {
  component: TFormComponent;
  mode: 'edit' | 'preview' | 'test';
  formTemplateId?: string;
  formValue?: any; // Current form value (selected option value for radio/select, array for checkbox)
  onNotesChange?: (notes: string) => void;
  onAttachmentChange?: (files: File[] | null) => void;
  notes?: string;
  attachments?: File[] | null;
}

export const ComponentActionFeatures: React.FC<IComponentActionFeaturesProps> = ({
  component,
  mode,
  formTemplateId,
  formValue,
  onNotesChange,
  onAttachmentChange,
  notes = '',
  attachments = null
}) => {
  const [localNotes, setLocalNotes] = useState<string>(notes);
  const [localAttachments, setLocalAttachments] = useState<File[] | null>(attachments);
  const [sendEmailChecked, setSendEmailChecked] = useState<boolean>(false);
  const [raiseIssueChecked, setRaiseIssueChecked] = useState<boolean>(false);
  const [showRaiseIssueModal, setShowRaiseIssueModal] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  // Reset state when formValue changes (option selection changes)
  useEffect(() => {
    setSendEmailChecked(false);
    setRaiseIssueChecked(false);
    setEmailSent(false);
    setShowRaiseIssueModal(false);
    setLocalNotes('');
    setLocalAttachments(null);
  }, [formValue]);

  // All hooks must be declared before any conditional returns
  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setLocalNotes(value);
    if (onNotesChange) {
      onNotesChange(value);
    }
  }, [onNotesChange]);

  const handleAttachmentChange = useCallback((change: any) => {
    if (change.value) {
      const files = Array.isArray(change.value) ? change.value : [change.value];
      const fileObjects = files.filter((f: any) => f instanceof File) as File[];
      setLocalAttachments(fileObjects.length > 0 ? fileObjects : null);
      if (onAttachmentChange) {
        onAttachmentChange(fileObjects.length > 0 ? fileObjects : null);
      }
    } else {
      setLocalAttachments(null);
      if (onAttachmentChange) {
        onAttachmentChange(null);
      }
    }
  }, [onAttachmentChange]);

  const handleSendEmailChange = useCallback((checked: boolean) => {
    setSendEmailChecked(checked);
    if (checked) {
      // TODO: Call send email API when available
      setTimeout(() => {
        setEmailSent(true);
        setTimeout(() => {
          setEmailSent(false);
        }, 3000);
      }, 500);
    }
  }, []);

  const handleRaiseIssueChange = useCallback((checked: boolean) => {
    setRaiseIssueChecked(checked);
    if (checked) {
      setShowRaiseIssueModal(true);
    }
  }, []);

  const handleRaiseIssueClose = useCallback(() => {
    setShowRaiseIssueModal(false);
    setRaiseIssueChecked(false);
  }, []);

  const handleRaiseIssueSuccess = useCallback(() => {
    setShowRaiseIssueModal(false);
    setRaiseIssueChecked(false);
  }, []);

  // Only show in test mode - this check must come AFTER all hooks
  if (mode !== 'test') {
    return null;
  }

  // Check if this is an option-based component (radio, checkbox, select, segment)
  const isOptionBasedComponent = ['select', 'radio', 'checkbox', 'segment'].includes(component.name || '');
  
  // Get options from multiple possible locations - prioritize basic.options as that's where they're stored
  const componentOptions = (component as any).basic?.options || (component as any).options || [];

  // For option-based components, check if any selected option has action features enabled
  let enableNotes = false;
  let enableAttachment = false;
  let enableRaiseIssue = false;
  let enableSendEmail = false;
  let selectedOption: any = null;

  // Check if formValue is valid (not undefined, null, empty string, or empty array)
  const hasValidFormValue = formValue !== undefined && 
                            formValue !== null && 
                            formValue !== '' && 
                            !(Array.isArray(formValue) && formValue.length === 0);

  // Helper function to evaluate threshold conditions
  const evaluateThresholdCondition = (condition: any, value: any): boolean => {
    const { operator, value: conditionValue } = condition;
    const stringValue = String(value).toLowerCase();
    const stringConditionValue = String(conditionValue).toLowerCase();

    switch (operator) {
      case 'equals':
        return stringValue === stringConditionValue || String(value) === String(conditionValue);
      case 'notEquals':
        return stringValue !== stringConditionValue && String(value) !== String(conditionValue);
      case 'greaterThan':
        return Number(value) > Number(conditionValue);
      case 'lessThan':
        return Number(value) < Number(conditionValue);
      case 'greaterThanOrEqual':
        return Number(value) >= Number(conditionValue);
      case 'lessThanOrEqual':
        return Number(value) <= Number(conditionValue);
      default:
        return false;
    }
  };

  // Check threshold conditions first (for all option-based components)
  const threshold = (component as any).threshold;
  const hasThresholdConditions = threshold && threshold.conditions && threshold.conditions.length > 0;
  
  if (hasThresholdConditions && hasValidFormValue) {
    // For checkbox, formValue is an array - check if any selected value matches a condition
    if (component.name === 'checkbox' && Array.isArray(formValue)) {
      // Check each selected value against all conditions
      const selectedValues = formValue;
      for (const value of selectedValues) {
        const matchingCondition = threshold.conditions.find((condition: any) => 
          evaluateThresholdCondition(condition, value)
        );
        
        if (matchingCondition) {
          // Enable action features based on the matching condition (OR logic - if any match, enable)
          // BUT: Don't show "Raise an Issue" here if threshold condition is met - ThresholdAlert handles it
          enableNotes = enableNotes || (matchingCondition.enableNotes === true);
          enableAttachment = enableAttachment || (matchingCondition.enableAttachment === true);
          // Skip enableRaiseIssue - ThresholdAlert will handle it when threshold condition is met
          enableSendEmail = enableSendEmail || (matchingCondition.enableSendEmail === true);
        }
      }
    } else if (isOptionBasedComponent) {
      // For radio, select, segment - formValue is a single value
      const matchingCondition = threshold.conditions.find((condition: any) => 
        evaluateThresholdCondition(condition, formValue)
      );

      if (matchingCondition) {
        // Enable action features based on the matching condition
        // BUT: Don't show "Raise an Issue" here if threshold condition is met - ThresholdAlert handles it
        enableNotes = matchingCondition.enableNotes === true;
        enableAttachment = matchingCondition.enableAttachment === true;
        // Skip enableRaiseIssue - ThresholdAlert will handle it when threshold condition is met
        enableSendEmail = matchingCondition.enableSendEmail === true;
      }
    } else {
      // For non-option-based components, check conditions
      const matchingCondition = threshold.conditions.find((condition: any) => 
        evaluateThresholdCondition(condition, formValue)
      );

      if (matchingCondition) {
        // Enable action features based on the matching condition
        // BUT: Don't show "Raise an Issue" here if threshold condition is met - ThresholdAlert handles it
        enableNotes = matchingCondition.enableNotes === true;
        enableAttachment = matchingCondition.enableAttachment === true;
        // Skip enableRaiseIssue - ThresholdAlert will handle it when threshold condition is met
        enableSendEmail = matchingCondition.enableSendEmail === true;
      }
    }
  }

  // Only check Basic/option-level properties if NO threshold conditions are defined
  // If threshold conditions exist, we ONLY use conditions (ignore Basic tab completely)
  // This means if conditions exist but don't match, we still don't check Basic tab
  if (!hasThresholdConditions) {
    if (isOptionBasedComponent && hasValidFormValue) {
      // Find the selected option(s)
      if (component.name === 'checkbox') {
        // For checkbox, formValue is an array
        const selectedValues = Array.isArray(formValue) ? formValue : [formValue];
        // Check if any selected option has action features enabled
        selectedValues.forEach((value: any) => {
          // Try to find the option by matching value (handle string/number conversion)
          const option = componentOptions.find((opt: any) => {
            if (!opt || !opt.value) return false;
            // Try exact match first
            if (opt.value === value) return true;
            // Try string comparison
            if (String(opt.value) === String(value)) return true;
            // Try label match as fallback
            if (opt.label === value || String(opt.label) === String(value)) return true;
            return false;
          });
          
          if (option) {
            enableNotes = enableNotes || (option.enableNotes === true);
            enableAttachment = enableAttachment || (option.enableAttachment === true);
            enableRaiseIssue = enableRaiseIssue || (option.enableRaiseIssue === true);
            enableSendEmail = enableSendEmail || (option.enableSendEmail === true);
            // Use the first selected option for display purposes
            if (!selectedOption) {
              selectedOption = option;
            }
          }
        });
      } else {
        // For radio, select, segment - formValue is a single value
        selectedOption = componentOptions.find((opt: any) => {
          if (!opt || !opt.value) return false;
          // Try exact match first
          if (opt.value === formValue) return true;
          // Try string comparison
          if (String(opt.value) === String(formValue)) return true;
          // Try label match as fallback
          if (opt.label === formValue || String(opt.label) === String(formValue)) return true;
          return false;
        });
        
        if (selectedOption) {
          enableNotes = selectedOption.enableNotes === true;
          enableAttachment = selectedOption.enableAttachment === true;
          enableRaiseIssue = selectedOption.enableRaiseIssue === true;
          enableSendEmail = selectedOption.enableSendEmail === true;
        }
      }
    } else {
      // For non-option-based components, use component-level settings
      enableNotes = component.basic?.enableNotes === true;
      enableAttachment = component.basic?.enableAttachment === true;
      enableRaiseIssue = component.basic?.enableRaiseIssue === true;
      enableSendEmail = component.basic?.enableSendEmail === true;
    }
  }

  // If no action features are enabled, don't render anything
  if (!enableNotes && !enableAttachment && !enableRaiseIssue && !enableSendEmail) {
    return null;
  }

  return (
    <>
      <div className="component-action-features">
        {/* Notes Section */}
        {enableNotes && (
          <div className="action-feature-section">
            <label className="form-label">
              Notes <span className="required-indicator">*</span>
            </label>
            <textarea
              className="action-notes-textarea"
              value={localNotes}
              onChange={handleNotesChange}
              placeholder="Explain which protocols were not followed"
              rows={4}
            />
            <div className="action-notes-character-count">
              {localNotes.length} / 50 characters minimum
            </div>
          </div>
        )}

        {/* Attachment Section */}
        {enableAttachment && (
          <div className="action-feature-section">
            <label className="form-label">
              Attachments
            </label>
            <DfFormFileUpload
              id={`${component.id}-attachment`}
              properties={{
                id: `${component.id}-attachment`,
                _id: `${component.id}-attachment`,
                name: 'file-upload',
                category: 'Basic',
                basic: {
                  label: 'Attachments',
                  placeholder: 'Click to upload or drag files',
                  value: '',
                  defaultValue: '',
                  options: [],
                  description: 'PDF, JPG, PNG up to 10MB'
                },
                validation: {
                  required: false,
                  customValidationMessage: '',
                  readonly: false,
                  multiple: true,
                  maxFiles: 10,
                  maxSize: 10 * 1024 * 1024, // 10MB
                  allowedTypes: ['.pdf', '.jpg', '.jpeg', '.png', 'image/*', 'application/pdf']
                },
                styles: {
                  labelAlignment: 'top'
                },
                position: 0,
                options: [],
                conditional: {
                  action: 'always',
                  when: 'all',
                  conditions: []
                }
              } as any}
              formValue={localAttachments}
              mode="edit"
              onValueChange={handleAttachmentChange}
              hideLabel={true}
            />
          </div>
        )}

        {/* Send Email and Raise Issue Checkboxes */}
        {(enableSendEmail || enableRaiseIssue) && (
          <div className="action-feature-section action-checkboxes-section">
            <div className="action-checkboxes-row">
              {enableSendEmail && (
                <div className="action-checkbox-item">
                  {emailSent ? (
                    <div className="action-email-sent-container">
                      <Check size={16} className="action-email-sent-icon" />
                      <span className="action-email-sent-text">Email sent</span>
                    </div>
                  ) : (
                    <label className="action-checkbox-label">
                      <input
                        type="checkbox"
                        checked={sendEmailChecked}
                        onChange={(e) => handleSendEmailChange(e.target.checked)}
                        className="action-checkbox-input"
                      />
                      <span>Send Email</span>
                    </label>
                  )}
                </div>
              )}

              {enableRaiseIssue && (
                <div className="action-checkbox-item">
                  <label className="action-checkbox-label">
                    <input
                      type="checkbox"
                      checked={raiseIssueChecked}
                      onChange={(e) => handleRaiseIssueChange(e.target.checked)}
                      className="action-checkbox-input"
                    />
                    <span>Raise an Issue</span>
                  </label>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Raise Issue Modal */}
      {showRaiseIssueModal && formTemplateId && (
        <RaiseIssueModal
          isOpen={showRaiseIssueModal}
          onClose={handleRaiseIssueClose}
          onSuccess={handleRaiseIssueSuccess}
          component={selectedOption ? {
            ...component,
            basic: {
              ...component.basic,
              label: `${component.basic?.label || 'Component'} - ${selectedOption.label || selectedOption.value}`,
              value: selectedOption.value || component.basic?.value || ''
            }
          } : component}
          formTemplateId={formTemplateId}
          notes={localNotes}
          attachments={localAttachments}
        />
      )}
    </>
  );
};

